import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import { SortId, sortValues } from 'consts/sort';
import { MappedTrait } from 'types/MappedTrait';
import type { GetPageLayoutResult, ServerSideProps as PageLayoutServerSideProps } from '@layouts/PageLayout';
import { serverRequests as pageLayoutServerRequests } from '@layouts/PageLayout/PageLayout.utils';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import withDescriptionData from 'core/hocs/withDescriptionData';
import { searchWithFiltersRoute } from 'apiRoutes/search';
import { getVariantSelectorRoute, getVariantBreadcrumbsRoute } from 'apiRoutes/variants';
import GetVariantUrlNameQuery, {
  GetVariantUrlNameResult,
  GetVariantUrlNameVariables,
} from 'graphql/queries/GetVariantUrlName.query';
import {
  getLeaselinkBankGroupsRequestConfig,
  GetLeaselinkBankGroupsResult,
} from 'graphql/queries/GetLeaselinkBankGroups.query';
import { productRequestConfig, GetProductResult, GetProductVariables } from 'graphql/queries/GetProduct.query';
import {
  pageFilesRequestConfig,
  GetPagesFilesResults,
  GetPagesFilesVariables,
} from 'graphql/queries/GetPagesFiles.query';
import ProductPage, { Props as ProductPageProps } from '@pages/Product';
import { getProductConstantFilters, getVariantVideo, getMappedMedia, getMappedPackageContents } from 'utils/product';
import handleError from 'utils/handleError';
import { serverApiFetch } from 'utils/fetch';
import graphQLRequest from 'utils/graphQLRequest';
import { logPerformanceEnd, logPerformanceStart } from 'utils/performance';
import combineRequests, { RequestConfig } from 'utils/combineRequest';
import { startTransaction } from 'utils/startTransaction';
import { captureException } from 'utils/captureException';
import getVariantAttributes from 'utils/getVariantAttributes';
import getVariantThumbnail from 'utils/getVariantThumbnail';
import mapSimpleVariant, { mapSimpleVariantWithCategories } from 'utils/mapSimpleVariant';
import getMappedTags from 'utils/getMappedTags';
import map from 'utils/map';
import omit from 'utils/omit';
import pick from 'utils/pick';

const { routes } = config;

const serverRequests: RequestConfig[] = [
  ...pageLayoutServerRequests,
  pageFilesRequestConfig,
  productRequestConfig,
  getLeaselinkBankGroupsRequestConfig,
];

type Result = PageLayoutServerSideProps & {
  productPageProps: ProductPageProps;
};

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(
  withDescriptionData(
    async ({ res, params }, { client, shopId }) => {
      const productId = params?.productId;
      const isPreview = params?.name === 'preview';
      const id = Number(productId);

      if (!id) {
        return handleError(res, 404);
      }

      const transaction = startTransaction({ op: 'server.requests', name: routes.product.id.href });
      const combinedDataSpan = transaction.startChild({ op: 'combined-data' });
      const { data: combinedData } = await graphQLRequest<
        GetPageLayoutResult & GetProductResult & GetLeaselinkBankGroupsResult & GetPagesFilesResults,
        GetProductVariables & GetPagesFilesVariables
      >(client, combineRequests(serverRequests), { variables: { id, pages: [routes.product.id.href] } });
      combinedDataSpan.finish();

      if (!combinedData) {
        transaction.finish();
        return handleError(res, 500);
      }

      const variant = combinedData?.variants[0];
      const shopVariant = combinedData?.shopsVariants[0];
      const leaselinkBankGroup = combinedData?.leaselink[0]?.bankGroups[0];

      if (!variant || !shopVariant) {
        transaction.finish();
        return handleError(res, 404);
      }

      if (variant.product.isConfigurable) {
        return {
          redirect: {
            destination: `/model/${variant.product.urlSlug}/${variant.product.id}`,
            permanent: true,
          },
        };
      }

      if (variant?.redirectVariantId) {
        const { data: redirectVariantData } = await graphQLRequest<GetVariantUrlNameResult, GetVariantUrlNameVariables>(
          client,
          GetVariantUrlNameQuery,
          { variables: { id: variant.redirectVariantId } }
        );
        const urlSlug = redirectVariantData?.variants[0].urlSlug || params?.name || '';
        transaction.finish();
        return {
          redirect: {
            destination: `/product/${urlSlug}/${variant.redirectVariantId}`,
            permanent: true,
          },
        };
      }

      if (!variant.urlSlug && !isPreview) {
        transaction.finish();
        captureException(new Error(`variant without url slug (id: ${id})`));
        return handleError(res, 503);
      }

      if (variant.urlSlug !== params?.name && !isPreview) {
        transaction.finish();
        return {
          redirect: {
            destination: `/product/${variant.urlSlug}/${id}`,
            permanent: true,
          },
        };
      }

      if (!variant.price.amount && shopVariant.isForSale && !isPreview) {
        transaction.finish();
        captureException(new Error(`Tried to enter 0 price variant (id: ${id})`));
        return handleError(res, 503);
      }

      if (shopVariant?.isGone) {
        transaction.finish();
        return handleError(res, 410);
      }

      if (shopVariant?.isHidden && !isPreview) {
        transaction.finish();
        return handleError(res, 503);
      }

      if (!variant.media.length && !variant.product.media.length) {
        captureException(new Error(`variant without media (id: ${id})`));
      }

      logPerformanceStart('breadcrumbs');
      const { data: breadcrumbsData } = await serverApiFetch(
        getVariantBreadcrumbsRoute,
        { id, isPreview },
        { headers: { 'x-commerce-shop-id': shopId } }
      );
      logPerformanceEnd('breadcrumbs');

      const breadcrumbs = breadcrumbsData?.breadcrumbs || [];

      const { data: searchWithFiltersData } = await serverApiFetch(
        searchWithFiltersRoute,
        {
          name: '',
          mode: 'category',
          limit: 20,
          offset: 0,
          order: sortValues[SortId.Popularity],
          filters: [{ key: 'product', values: [variant.product.name] }],
        },
        { headers: { 'x-commerce-shop-id': shopId } }
      );

      logPerformanceStart('selector');
      const { data: result } = await serverApiFetch(
        getVariantSelectorRoute,
        { id },
        { headers: { 'x-commerce-shop-id': shopId } }
      );
      logPerformanceEnd('selector');

      const optionGroups = result?.optionGroups || [];
      const selectedOptions = result?.selectedOptions || {};

      const { finalDescription, ...variantAttributes } = getVariantAttributes(variant, shopId, 'MAIN_ATTRIBUTE');

      transaction.finish();

      const fabBottomOffsetMap = [
        { condition: variant.isEol },
        { value: 88, condition: variant.availability && shopVariant.isForSale },
        { value: 48, condition: shopVariant.isForSale },
      ];

      const video = getVariantVideo(variant.product, variant.media);

      const traitValues = map(variant.product.traitValues || [], 'traitValueId');

      const traits: MappedTrait[] =
        variant.product.traits?.map(({ trait }) => ({
          ...trait,
          traitValues: trait.traitValues.map((traitValue) => ({
            ...traitValue,
            isSelected: traitValues.includes(traitValue.id),
          })),
        })) || [];

      const productPageProps: ProductPageProps = {
        ...pick(variant, [
          'id',
          'urlSlug',
          'names',
          'price',
          'grossPrice',
          'priceRrp',
          'producerPn',
          'productCode',
          'ean',
          'isEol',
          'availability',
          'rating',
          'ratingCount',
          'shipsUntil',
          'currentSuppliers',
          'documents',
          'isPrerelease',
          'isLeasingAvailable',
        ]),
        leasingMinAmount: leaselinkBankGroup?.minAmount || null,
        variants: searchWithFiltersData?.search || [],
        hits: searchWithFiltersData?.hits || 0,
        constantFilters: getProductConstantFilters(
          variant.product,
          searchWithFiltersData?.hits,
          searchWithFiltersData?.filters
        ),
        filters: searchWithFiltersData?.filters || [],
        traits,
        description: finalDescription,
        thumbnail: getVariantThumbnail({ thumbnail: variant.thumbnail, product: variant.product }),
        ...pick(shopVariant, ['isForSale']),
        product: pick(variant.product, ['id', 'name']),
        video,
        ...variantAttributes,
        tags: getMappedTags(variant.variantsTags),
        pictures: getMappedMedia(variant.product, variant.media, variant.names, shopId),
        packageContents: getMappedPackageContents(variant.variantsPackageContents),
        complementaryVariants: variant.variantsComplementaryVariants.map(({ complementaryVariant }) =>
          mapSimpleVariant(complementaryVariant, shopId, 'COMPLEMENTARY_VARIANT')
        ),
        recommendedVariants: variant.variantsRecommendedVariants.map(({ recommendedVariant }) =>
          mapSimpleVariantWithCategories(recommendedVariant)
        ),
        productType: variant.product.type,
        manufacturer: variant.product.manufacturer,
        optionGroups,
        selectedOptions,
      };

      return {
        props: {
          ...omit(combinedData, ['variants', 'shopsVariants']),
          productPageProps,
          breadcrumbs,
          compareFabBottomOffset: fabBottomOffsetMap.find(({ condition }) => condition)?.value || 0,
        },
      };
    },
    { descriptionsGetter: ({ productPageProps }) => [productPageProps.description] }
  )
);

const Product = ({ productPageProps }: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <ProductPage {...productPageProps} />
);

export default withPageLayout(Product, { showBreadcrumb: true, breadcrumbLinkVariant: 'real' });
